import React, { useState, useEffect } from "react";
import { jobsEndpoint } from "../constants";

// Placeholder images - replace with your actual imports
const Avatar = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face&auto=format";
const Avatar2 = "https://images.unsplash.com/photo-1494790108755-2616b7e3de5b?w=40&h=40&fit=crop&crop=face&auto=format";
const Avatar3 = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face&auto=format";
const Avatar4 = "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=40&h=40&fit=crop&crop=face&auto=format";
const Avatar5 = "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face&auto=format";
const GoogleIcon = "https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/google.svg";

const HiringPipeline = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJobId, setSelectedJobId] = useState("");
  const [jobs, setJobs] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Define pipeline stages that match your Django status choices
  const pipelineStages = [ "APPLIED", "SHORTLISTED", "INTERVIEW_SCHEDULED", "OFFER_SENT","HIRED"];


  // Fetch jobs list
  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token'); // Adjust based on your auth implementation
      const response = await fetch(`${jobsEndpoint}list`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch jobs');
      
      const data = await response.json();
      setJobs(data.results || data); // Adjust based on your API response structure
    } catch (err) {
      setError('Failed to load jobs');
      console.error('Error fetching jobs:', err);
    }
  };

  // Fetch applicants for a specific job and status
  const fetchApplicantsByStatus = async (jobId, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${jobsEndpoint}${jobId}/applicants/${status.toLowerCase()}/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error(`Failed to fetch ${status} applicants`);
      
      const data = await response.json();
      return data.applicants || [];
    } catch (err) {
      console.error(`Error fetching ${status} applicants:`, err);
      return [];
    }
  };

  // Fetch all applicants for selected job
  const fetchAllApplicants = async (jobId) => {
    if (!jobId) {
      setApplicants([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch applicants for each status in parallel
      const promises = pipelineStages.map(status => 
        fetchApplicantsByStatus(jobId, status)
      );
      
      const results = await Promise.all(promises);
      
      // Combine all applicants and add stage property
      const allApplicants = [];
      results.forEach((applicantList, index) => {
        const stage = pipelineStages[index];
        applicantList.forEach(applicant => {
          allApplicants.push({
            ...applicant,
            stage: stage,
            // Map API response to component format
            name: applicant.user?.first_name && applicant.user?.last_name 
              ? `${applicant.user.first_name} ${applicant.user.last_name}`
              : applicant.user?.email || 'Unknown',
            email: applicant.user?.email || '',
            position: applicant.job_title || '',
            company: applicant.company_name || '',
            location: applicant.user?.profile?.location || 'Not specified',
            experience: applicant.user?.profile?.experience || 'Not specified',
            salary: applicant.user?.profile?.expected_salary || 0,
            avatar: applicant.user?.profile?.avatar || Avatar3,
            cv_files: applicant.cv_files || [],
            extra_files: applicant.extra_files || []
          });
        });
      });
      
      setApplicants(allApplicants);
    } catch (err) {
      setError('Failed to load applicants');
      console.error('Error fetching applicants:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle job selection
  const handleJobSelect = (e) => {
    const jobId = e.target.value;
    setSelectedJobId(jobId);
    if (jobId) {
      fetchAllApplicants(jobId);
    } else {
      setApplicants([]);
    }
  };

  // Filter applicants based on search term
  const filteredApplicants = applicants.filter(applicant =>
    applicant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    applicant.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    applicant.position.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format salary
  const formatSalary = (salary) => {
    if (!salary || salary === 0) return 'Not specified';
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(salary) + "/annum";
  };

  // Load jobs on component mount
  useEffect(() => {
    fetchJobs();
  }, []);

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex gap-6 justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Hiring Pipeline
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Here is a complete overview of your jobs.
            </p>
          </div>

        </div>
      </div>

      {/* Search and Select Job */}
      <div className="flex lg:flex-row w-full flex-col gap-6 lg:items-center items-end justify-start py-5">
        <div className="flex md:flex-row flex-col gap-6 md:items-center items-start lg:w-4/5 w-full lg:justify-start justify-between">
          <div className="relative w-full">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search candidates..."
              className="md:w-[320px] w-full px-4 py-2 pl-10 pr-4 text-gray-700 bg-transparent border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#19BFB7] focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Select Job Input */}
          <div className="py-2 px-4 text-gray-700 border border-gray-300 rounded-lg">
            <select value={selectedJobId} onChange={handleJobSelect} className="bg-transparent outline-none">
              <option value="">Select job</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.title} - {job.company?.name || 'Unknown Company'}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Users Pic */}
        <div className="lg:w-auto w-full">
          <div className="flex relative items-center">
            <div className="">
              <img src={Avatar} alt="" className="w-10 h-10 rounded-full" />
            </div>
            <div>
              <img src={Avatar2} alt="" className="absolute left-[30px] top-[1px] border-2 border-white rounded-full z-40 w-10 h-10" />
            </div>
            <div>
              <img src={Avatar3} alt="" className="absolute left-[60px] top-[1px] border-2 border-white rounded-full z-40 w-10 h-10" />
            </div>
            <div>
              <img src={Avatar4} alt="" className="absolute left-[90px] top-[2px] border-2 border-white rounded-full z-40 w-10 h-10" />
            </div>
            <div>
              <img src={Avatar5} alt="" className="absolute left-[120px] top-[2px] border-2 border-white rounded-full z-40 w-10 h-10" />
            </div>
            <div className="absolute font-semibold top-[4px] left-[150px] bg-gray-100 text-gray-700 border-white rounded-full z-40 p-2 flex items-center w-10 h-10 justify-center">
              <span>+5</span>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#19BFB7]"></div>
          <span className="ml-2 text-gray-600">Loading applicants...</span>
        </div>
      )}

      {/* Pipeline Stages */}
      <div className="grid grid-cols-1 lg:grid-cols-5 md:grid-cols-3 gap-4 py-4">
        {pipelineStages.map((stage) => {
          const stageCandidates = filteredApplicants.filter((c) => c.stage === stage);

          return (
            <div key={stage} className="flex flex-col w-full rounded-md">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-900 font-semibold text-sm">
                  {stage.replace('_', ' ')}
                </span>
                <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                  {stageCandidates.length}
                </span>
              </div>

              <div className="space-y-2">
                {stageCandidates.length > 0 ? (
                  stageCandidates.map((candidate, index) => (
                    <div key={candidate.id || index} className="p-3 bg-white rounded-md shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2 border-b border-gray-200 pb-3">
                        <div>
                          <img 
                            src={candidate.avatar} 
                            alt={candidate.name}
                            className="w-8 h-8 rounded-full object-cover"
                            onError={(e) => { e.target.src = Avatar3; }}
                          />
                        </div>
                        <div>
                          <span className="text-gray-700 font-medium text-sm">
                            {candidate.name}
                          </span>
                          <p className="text-xs text-gray-500">
                            {candidate.email}
                          </p>
                        </div>
                      </div>

                      <div className="py-3 border-b border-gray-200">
                        <h3 className="text-gray-500 text-xs">
                          Applied for & Company
                        </h3>
                        <div className="flex items-center gap-1 text-gray-700 font-semibold text-xs py-2">
                          <img src={GoogleIcon} alt="" className="w-4 h-4" />
                          <p>{candidate.position}</p> at
                          <p>{candidate.company}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-3 py-2">
                          <div>
                            <h5 className="text-gray-500 text-xs font-normal">
                              Location
                            </h5>
                            <span className="text-gray-700 font-medium text-xs">
                              {candidate.location}
                            </span>
                          </div>
                          <div>
                            <h5 className="text-gray-500 text-xs font-normal">
                              Experience
                            </h5>
                            <span className="text-gray-700 font-medium text-xs">
                              {candidate.experience}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-2">
                          <div>
                            <h5 className="text-gray-500 text-xs font-normal">
                              Expected Salary
                            </h5>
                            <span className="text-gray-700 font-medium text-xs">
                              {formatSalary(candidate.salary)}
                            </span>
                          </div>
                          <div>
                            <h5 className="text-gray-500 text-xs font-normal">
                              Files
                            </h5>
                            <span className="text-gray-700 font-medium text-xs">
                              {candidate.cv_files.length + candidate.extra_files.length} files
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="pt-3 flex items-center justify-between">
                        <div className="relative">
                          <img src={Avatar4} alt="" className="w-7 h-7 rounded-full" />
                          <img src={Avatar5} alt="" className="absolute w-7 h-7 z-50 left-[14px] top-0 rounded-full border-2 border-white" />
                          <img src={Avatar2} alt="" className="absolute w-7 h-7 z-50 left-[32px] top-0 rounded-full border-2 border-white" />
                        </div>

                        <div className="flex items-center gap-2 cursor-pointer">
                          <p className="text-[#009C97] text-xs font-normal">
                            View Details
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <span className="text-gray-400 text-xs">No Candidates</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* No Job Selected Message */}
      {!selectedJobId && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg mb-2">📋</div>
          <p className="text-gray-500">Select a job to view applicants</p>
        </div>
      )}
    </div>
  );
};

export default HiringPipeline;